import {
  calculateTimeDifferenceFormatted,
  pickPointsAtTargetTimes,
} from "../utils/formatters";
import { getChartColors } from "../theme/tellorTheme";

export const prepareRollingAverageChart = (
  dataset,
  timeScale,
  includeBlockTime,
  customStartDate,
  customEndDate,
  avgBlockTime = 0,
  mode = 'light'
) => {
  if (!Array.isArray(dataset) || dataset.length === 0)
    return { labels: [], datasets: [] };
  const data = [...dataset].reverse();
  let processedData;
  switch (timeScale) {
    case "recent": {
      // Last 10 data points
      const recentData = data.slice(-10);

      const delays = recentData.map((item) => {
        const reportDate = new Date(item.reportTimestamp);
        const relayDate = new Date(item.relayTimestamp);
        let delayInSeconds = (relayDate - reportDate) / 1000;
        if (includeBlockTime && avgBlockTime > 0) {
          delayInSeconds = Math.max(0, delayInSeconds - avgBlockTime);
        }
        return delayInSeconds;
      });

      const rollingAverages = delays.map((_, index) => {
        const subset = delays.slice(0, index + 1);
        return subset.reduce((sum, delay) => sum + delay, 0) / subset.length;
      });

      processedData = {
        labels: recentData.map((item) =>
          new Date(item.reportTimestamp).toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "numeric",
            hour12: true,
          })
        ),
        delays: delays,
        averageDelay: rollingAverages,
      };
      break;
    }
    case "daily": {
      // Last 10 days, one point per day at same timestamp (noon UTC)
      const targetTimestamps = [];
      const now = new Date();
      for (let i = 9; i >= 0; i--) {
        const d = new Date(now);
        d.setUTCDate(d.getUTCDate() - i);
        d.setUTCHours(12, 0, 0, 0);
        targetTimestamps.push(d.getTime());
      }
      const recentData = pickPointsAtTargetTimes(data, targetTimestamps);

      const delays = recentData.map((item) => {
        const reportDate = new Date(item.reportTimestamp);
        const relayDate = new Date(item.relayTimestamp);
        let delayInSeconds = (relayDate - reportDate) / 1000;
        if (includeBlockTime && avgBlockTime > 0) {
          delayInSeconds = Math.max(0, delayInSeconds - avgBlockTime);
        }
        return delayInSeconds;
      });

      const rollingAverages = delays.map((_, index) => {
        const subset = delays.slice(0, index + 1);
        return subset.reduce((sum, delay) => sum + delay, 0) / subset.length;
      });

      processedData = {
        labels: recentData.map((item) => {
          const date = new Date(item.reportTimestamp);
          return date.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
        }),
        delays: delays,
        averageDelay: rollingAverages,
      };
      break;
    }
    case "weekly": {
      // Last 10 weeks, one point per week at same timestamp (noon UTC, same day)
      const targetTimestamps = [];
      const now = new Date();
      for (let i = 9; i >= 0; i--) {
        const d = new Date(now);
        d.setUTCDate(d.getUTCDate() - i * 7);
        d.setUTCHours(12, 0, 0, 0);
        targetTimestamps.push(d.getTime());
      }
      const recentData = pickPointsAtTargetTimes(data, targetTimestamps);

      const delays = recentData.map((item) => {
        const reportDate = new Date(item.reportTimestamp);
        const relayDate = new Date(item.relayTimestamp);
        let delayInSeconds = (relayDate - reportDate) / 1000;
        if (includeBlockTime && avgBlockTime > 0) {
          delayInSeconds = Math.max(0, delayInSeconds - avgBlockTime);
        }
        return delayInSeconds;
      });

      const rollingAverages = delays.map((_, index) => {
        const subset = delays.slice(0, index + 1);
        return subset.reduce((sum, delay) => sum + delay, 0) / subset.length;
      });

      processedData = {
        labels: recentData.map((item) => {
          const date = new Date(item.reportTimestamp);
          return date.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
        }),
        delays: delays,
        averageDelay: rollingAverages,
      };
      break;
    }
    case "custom": {
      // Handle custom date range
      if (!customStartDate || !customEndDate) {
        // If no custom dates set, fall back to weekly view (10 weeks, same timestamp)
        const targetTimestamps = [];
        const now = new Date();
        for (let i = 9; i >= 0; i--) {
          const d = new Date(now);
          d.setUTCDate(d.getUTCDate() - i * 7);
          d.setUTCHours(12, 0, 0, 0);
          targetTimestamps.push(d.getTime());
        }
        const recentData = pickPointsAtTargetTimes(data, targetTimestamps);

        const delays = recentData.map((item) => {
          const reportDate = new Date(item.reportTimestamp);
          const relayDate = new Date(item.relayTimestamp);
          let delayInSeconds = (relayDate - reportDate) / 1000;
          if (includeBlockTime && avgBlockTime > 0) {
            delayInSeconds = Math.max(0, delayInSeconds - avgBlockTime);
          }
          return delayInSeconds;
        });

        const rollingAverages = delays.map((_, index) => {
          const subset = delays.slice(0, index + 1);
          return subset.reduce((sum, delay) => sum + delay, 0) / subset.length;
        });

        processedData = {
          labels: recentData.map((item) =>
            new Date(Number(item.reportTimestamp)).toLocaleString("en-US", {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
          ),
          delays: delays,
          averageDelay: rollingAverages,
        };
      } else {
        // Filter data by custom date range
        const startDate = new Date(customStartDate);
        const endDate = new Date(customEndDate);

        let customData = data.filter((item) => {
          const itemDate = new Date(item.reportTimestamp);
          return itemDate >= startDate && itemDate <= endDate;
        });

        // Limit to max 300 points to prevent overcrowding
        if (customData.length > 300) {
          const step = Math.ceil(customData.length / 300);
          customData = customData.filter((_, index) => index % step === 0);
        }

        const delays = customData.map((item) => {
          let timeDiff = calculateTimeDifferenceFormatted(
            item.reportTimestamp,
            item.relayTimestamp
          );
          let delay = parseFloat(timeDiff);
          if (timeDiff.includes("m")) {
            const [min, sec] = timeDiff.split("m");
            delay = parseInt(min) * 60 + parseFloat(sec.replace("s", ""));
          }
          // Subtract block time if enabled
          if (includeBlockTime && avgBlockTime > 0) {
            delay = Math.max(0, delay - avgBlockTime);
          }
          return delay;
        });

        const rollingAverages = delays.map((_, index) => {
          const subset = delays.slice(0, index + 1);
          return subset.reduce((sum, delay) => sum + delay, 0) / subset.length;
        });

        processedData = {
          labels: customData.map((item) => {
            const date = new Date(item.reportTimestamp);
            return date.toLocaleString("en-US", {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            });
          }),
          delays: delays,
          averageDelay: rollingAverages,
        };
      }
      break;
    }
    default: {
      // Fall back to weekly view for any unexpected timeScale (10 weeks, same timestamp)
      const targetTimestamps = [];
      const now = new Date();
      for (let i = 9; i >= 0; i--) {
        const d = new Date(now);
        d.setUTCDate(d.getUTCDate() - i * 7);
        d.setUTCHours(12, 0, 0, 0);
        targetTimestamps.push(d.getTime());
      }
      const recentData = pickPointsAtTargetTimes(data, targetTimestamps);

      const delays = recentData.map((item) => {
        const reportDate = new Date(item.reportTimestamp);
        const relayDate = new Date(item.relayTimestamp);
        let delayInSeconds = (relayDate - reportDate) / 1000;
        if (includeBlockTime && avgBlockTime > 0) {
          delayInSeconds = Math.max(0, delayInSeconds - avgBlockTime);
        }
        return delayInSeconds;
      });

      const rollingAverages = delays.map((_, index) => {
        const subset = delays.slice(0, index + 1);
        return subset.reduce((sum, delay) => sum + delay, 0) / subset.length;
      });

      processedData = {
        labels: recentData.map((item) => {
          const date = new Date(item.reportTimestamp);
          return date.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
        }),
        delays: delays,
        averageDelay: rollingAverages,
      };
      break;
    }
  }

  const colors = getChartColors(mode);

  return {
    labels: processedData.labels,
    datasets: [
      {
        label: "Rolling Average",
        data: processedData.averageDelay,
        borderColor: colors.delaySecondary,
        backgroundColor: `${colors.delaySecondary}1A`,
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0,
      },
      {
        label: `${timeScale === "recent" ? "Current" : "Current"} Delay${
          includeBlockTime ? " (Block Time Adj.)" : ""
        } (sec)`,
        data: processedData.delays,
        borderColor: colors.delayPrimary,
        backgroundColor: `${colors.delayPrimary}33`,
        borderWidth: timeScale === "recent" ? 2 : 1,
        pointRadius: timeScale === "recent" ? 4 : 2,
        pointHoverRadius: timeScale === "recent" ? 6 : 4,
        pointBackgroundColor: colors.delayPrimary,
        pointBorderColor: colors.delayPrimary,
        tension: 0.1,
      },
    ],
  };
};
